import * as z from "zod";
import { Models } from "appwrite";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Button,
  Input,
  Textarea,
} from "@/components/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch";
import { PostValidation } from "@/lib/validation";
import { useToast } from "@/components/ui/use-toast";
import { useUserContext } from "@/context/AuthContext";
import { FileUploader, Loader } from "@/components/shared";
import { useCreatePost, useUpdatePost } from "@/lib/react-query/queries";
import { useEffect, useState } from "react";
import React from "react";
import { KYCVerificationModal } from "@/modals/KYCVerificationModal";
import { getKYCStatus } from "@/lib/appwrite/api";
import { CheckCircle2 } from "lucide-react";
import BusinessSLA from "@/_auth/forms/BusinessSLA";

type PostFormProps = {
  post?: Models.Document;
  action: "Create" | "Update";
};

const PostForm =  ({ post, action }: PostFormProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useUserContext();
  const [showKYCModal, setShowKYCModal] = useState(false);
  const [kycVerified, setKycVerified] = useState(false);
  const [loadingKYC, setLoadingKYC] = useState(true);
  const [showSellingToggle, setShowSellingToggle] = useState(
    post ? post.individual === "sell-something" : false
  );
  const [retainedMedia, setRetainedMedia] = useState<Array<{ fileId: string; url: string; mediaType: string }>>(() => {
    if (Array.isArray((post as any)?.media)) {
      return (post as any).media
        .filter((media: any) => media?.fileId && media?.url)
        .map((media: any) => ({ fileId: media.fileId, url: media.url, mediaType: media.mediaType || "application/octet-stream" }));
    }

    const urls = Array.isArray((post as any)?.imageUrl) ? (post as any).imageUrl : (post as any)?.imageUrl ? [(post as any).imageUrl] : [];
    const ids = Array.isArray((post as any)?.imageId) ? (post as any).imageId : (post as any)?.imageId ? [(post as any).imageId] : [];
    return urls.filter(Boolean).map((url: string, index: number) => ({ fileId: ids[index] ?? url, url, mediaType: "application/octet-stream" }));
  });

  useEffect(() => {
    const checkKYCStatus = async () => {
      try {
        const checkExistingKYC = await getKYCStatus(user.id);
        setKycVerified(
          checkExistingKYC.panVerified && 
          checkExistingKYC.bankVerified
        );
      } catch (error) {
        console.error("Failed to fetch KYC status:", error);
        toast({
          title: "Error",
          description: "Could not verify KYC status",
          variant: "destructive",
        });
      } finally {
        setLoadingKYC(false);
      }
    };

    checkKYCStatus();
  }, [user.id, toast]);

  const form = useForm<z.infer<typeof PostValidation>>({
    resolver: zodResolver(PostValidation),
    defaultValues: {
      individual: post ? post?.individual : "",
      dealType: post ? post?.category : "",
      caption: post ? post?.caption : "",
      file: [],
      location: post?.location ?? "",
      tags: post ? post.tags.join(",") : "",
      isSellingPost: post ? post.isSellingPost : false
    },
  });

  // Query
  const { mutateAsync: createPost, isLoading: isLoadingCreate } =
    useCreatePost();
  const { mutateAsync: updatePost, isLoading: isLoadingUpdate } =
    useUpdatePost();

  const handleSubmit = async (value: z.infer<typeof PostValidation>) => {
    if (post && action === "Update") {
      const updatedPost = await updatePost({
        ...value,
        postId: post.$id,
        imageId: post.imageId,
        imageUrl: post.imageUrl,
        media: retainedMedia,
      });

      if (!updatedPost) {
        toast({
          title: `${action} post failed. Please try again.`,
        });
      }
      toast({
        title:`Post updation will take sometime for validation. Sit tight!`,
      });
      return navigate(`/posts/${post.$id}`);
    }

    // ACTION = CREATE
    const newPost = await createPost({
      ...value,
      userId: user.id,
    });

    if (!newPost) {
      toast({
        title: `${action} post failed. Please try again.`,
      });
    }
    toast({
      title:`Post will be visible on the home page after verification!`,
    });
    navigate("/home");
  };

  return (
    <>
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex flex-col gap-6 w-full max-w-5xl">
        
        {/* Show individual field for both Create and Update */}
        <FormField
          control={form.control}
          name="individual"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="shad-form_label">What do you want to do?</FormLabel>
              <Select 
                onValueChange={(value) => {
                  field.onChange(value);
                  setShowSellingToggle(value === "sell-something");
                }} 
                defaultValue={field.value}
                value={field.value}
              >
                <FormControl>
                  <SelectTrigger className="shad-select w-full md:w-[360px]">
                    <SelectValue placeholder="Select your purpose" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="sell-something">Sell Something</SelectItem>
                  <SelectItem value="marketize">Marketize (Promote Business, hiring, Skill,etc)</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage className="shad-form_message" />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="caption"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="shad-form_label">Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={showSellingToggle 
                    ? "Here you can include your terms and conditions, price per metric, delivery area, etc." 
                    : "Describe your business or what you're promoting"}
                  className="shad-textarea custom-scrollbar"
                  {...field}
                />
              </FormControl>
              <FormMessage className="shad-form_message" />
            </FormItem>
          )}
        />
          <FormField
            control={form.control}
            name="dealType"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="shad-form_label">Deal Type</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  value={field.value} // Ensure controlled component
                >
                  <FormControl>
                    <SelectTrigger className="shad-select w-full md:w-[360px]">
                      <SelectValue placeholder="Select deal type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                      <SelectItem value="physical-goods">Physical Goods</SelectItem>
                      <SelectItem value="services">Services</SelectItem>
                      <SelectItem value="digital">Digital Products</SelectItem>
                    <SelectItem value="Others">Others</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage className="shad-form_message" />
              </FormItem>
            )}
          />

        <FormField
          control={form.control}
          name="file"
          render={({ field }) => {
            return (
              <FormItem className="md:w-1/2 w-full">
                <FormLabel className="shad-form_label">
                  Add Photos or Video (Optional for text posts)
                </FormLabel>
                <FormControl>
                  <FileUploader
                    mode="post"
                    fieldChange={field.onChange}
                    existingMedia={retainedMedia}
                    onExistingMediaChange={setRetainedMedia}
                    maxFiles={6}
                  />
                </FormControl>
                <p className="text-light-4 small-regular mt-2">
                  Up to 6 photos or 1 video (60s max)
                </p>
                <FormMessage className="shad-form_message" />
              </FormItem>
            );
          }}
        />

        <FormField
          control={form.control}
          name="location"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="hidden shad-form_label">Add Location (Optional)</FormLabel>
              <FormControl>
                <Input type="text"
                 placeholder="e.g Gaya, Bihar"
                 className="hidden shad-input" {...field} />
              </FormControl>
              <FormMessage className="shad-form_message" />
            </FormItem>
          )}
        />

        {/* Show escrow KYC toggle when selling option is selected */}
        {showSellingToggle && (
          <FormField
            control={form.control}
            name="isSellingPost"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-1 pr-4">
                    <FormLabel>Enable Escrow KYC?</FormLabel>
                    {!kycVerified && (
                      <p className="text-xs text-light-3">
                        Complete KYC to receive money online.
                      </p>
                    )}
                  </div>
                  {loadingKYC ? (
                    <Loader  />
                  ) : (
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          if (checked && !kycVerified) {
                            setShowKYCModal(true);
                            return;
                          }
                          field.onChange(checked);
                        }}
                      />
                    </FormControl>
                  )}
                </div>
                {!kycVerified && field.value && (
                  <Button 
                    type="button" 
                    onClick={() => setShowKYCModal(true)}
                    className="w-fit"
                  >
                    Complete KYC Verification
                  </Button>
                )}
                {kycVerified && field.value && (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <CheckCircle2 size={16} />
                    <span>KYC Verified</span>
                    <p className="text-sm text-gray-600">
                      . By continuing, you are agreeing to <BusinessSLA />
                    </p>
                  </div>
                )}
              </FormItem>
        )}
    />
  )}
        
        <FormField
          control={form.control}
          name="tags"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="hidden shad-form_label">
                Add Tags (Optional, separated by comma)
              </FormLabel>
              <FormControl>
                <Input
                  placeholder={showSellingToggle 
                    ? "e.g product, sale, discount, electronics" 
                    : "e.g promotion, marketing, business, service"}
                  type="text"
                  className="hidden shad-input"
                  {...field}
                />
              </FormControl>
              <FormMessage className="shad-form_message" />
            </FormItem>
          )}
        />

        <div className="flex gap-4 items-center justify-end">
          <Button
            type="button"
            className="shad-button_dark_4"
            onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button
            type="submit"
            className="shad-button_primary whitespace-nowrap"
            disabled={isLoadingCreate || isLoadingUpdate}>
            {(isLoadingCreate || isLoadingUpdate) && <Loader />}
            {action} Post
          </Button>
        </div>
      </form>
    </Form>
    
    {showKYCModal && (
      <KYCVerificationModal
        userId={user.id}
        onComplete={(status) => {
          setKycVerified(status.pan && status.bank);
          setShowKYCModal(false);

          if (status.pan && status.bank) {
            form.setValue('isSellingPost', true, { shouldDirty: true });
          } else {
            toast({
              title: 'KYC Partially Completed',
              description: 'You can complete remaining verifications later',
            });
          }
        }}
        onClose={() => setShowKYCModal(false)}
      />
    )}
    </>
  );
};

export default PostForm;
